import * as React from 'react';
import * as _ from 'lodash';

import { DeveloperModel } from '@journeyapps-platform/lib-journey-core';
import * as microErrors from '@journeyapps-platform/micro-errors';

import { isAllowed } from '../../../../hooks/useAllowed';
import { useSystem } from '../../../../hooks/useSystem';
import {
  AbstractEditDeveloperDialogWidget,
  EditDeveloperSubmitParams,
  EditDeveloperValues,
  showErrorDialog
} from './AbstractEditDeveloperDialogWidget';
import { Routes, useJourneyNavigation } from '../../../../routes';
import { DialogStore } from '../../../../stores/DialogStore';
import { OrgStore } from '../../../../stores/OrgStore';
import { System } from '../../../../System';
import { createDeveloperInvite } from './EditDeveloperInviteDialogWidget';
import { UserModelAction } from '@journeyapps-platform/cardinal-catalog';

export type EditParentDeveloperDialogWidgetProps = {
  onSubmitOrCancel: () => any;
};

export async function createDeveloper(
  values: EditDeveloperValues,
  org_id: string,
  system: System,
  orgStore: OrgStore,
  dialogStore: DialogStore
) {
  const tempDev = system.developStore.generate({
    id: '',
    org_id: org_id,
    name: '',
    email: ''
  });

  const selectedRoles = _.chain(values.roleIds)
    .map((roleId) => orgStore.current_org?.roles.items.find((roleModel) => roleModel.id == roleId))
    .compact()
    .value();

  const allowedCreate = await isAllowed({ system: system, action: UserModelAction.CREATE, resource: tempDev });
  if (!allowedCreate) {
    await dialogStore.errorDialog({
      title: 'Not Allowed',
      errorMessage: `You do not have permission to add a developer. Ask your administrator to add this developer, or to elevate your permissions.`
    });
    return;
  }
  try {
    const newDev = await system.accountV5Client.createUser({ email: values.email, org_id });
    if (!newDev) {
      console.error(`Empty developer returned in response`, newDev);
      await showErrorDialog(dialogStore);
      return;
    }

    const developerReference = await system.developStore.resolve(newDev.id);

    for (let selectedRole of selectedRoles) {
      await developerReference.assignRole(selectedRole);
    }

    await developerReference.loadPolicies();

    orgStore.getOrg(org_id).then((o) => o.loadDevelopers());
  } catch (e) {
    if (microErrors.matchesErrorCode(e, 'RESOURCE_CONFLICT')) {
      // ResourceConflict - Email already taken invite the existing dev
      await createDeveloperInvite(values, org_id, system, orgStore, dialogStore);
    } else {
      console.error(`Failed to add developer`, e);
      await showErrorDialog(dialogStore, microErrors.getErrorData(e)?.details);
    }
  }
}

const onSubmit = async (params: EditDeveloperSubmitParams<DeveloperModel>) => {
  const { context, developerReference, stores, values } = params;

  const { model_id, org_id } = context;

  const { system, orgStore, dialogStore } = stores;

  const payload = {
    email: values.email,
    org_id: org_id
  };

  const tempDev = system.developStore.generate({
    id: model_id || '',
    ...payload,
    name: ''
  });

  const selectedRoles = _.chain(values.roleIds)
    .map((roleId) => orgStore.current_org?.roles.items.find((roleModel) => roleModel.id == roleId))
    .compact()
    .value();

  if (developerReference) {
    const allowedUpdate = await isAllowed({ system: system, action: UserModelAction.UPDATE, resource: tempDev });
    if (!allowedUpdate) {
      await dialogStore.errorDialog({
        title: 'Not Allowed',
        errorMessage: `You do not have permission to edit: ${values.email}`
      });
    }
    try {
      // Remove roles which have not been assigned
      const rolesToRemove = _.differenceBy(
        developerReference.roles.items
          .map((roleModel) => roleModel.role)
          .filter((role) => role.options.role.org_id == org_id),
        selectedRoles,
        (role) => role.id
      );

      for (let removedRole of rolesToRemove) {
        await developerReference.unAssignRole(removedRole);
      }

      // Assign all selected roles
      for (let selectedRole of selectedRoles) {
        await developerReference.assignRole(selectedRole);
      }

      orgStore.getOrg(org_id).then((o) => o.loadDevelopers());
    } catch (e) {
      console.error(`Failed to update developer`, e);
      await dialogStore.errorDialog({
        subTitle: 'Failed to update developer user',
        errorMessage: `Please check the console for more information`
      });
    }
  } else {
    await createDeveloper(values, context.org_id, system, orgStore, dialogStore);
  }
};

export const EditParentDeveloperDialogWidget: React.FC<EditParentDeveloperDialogWidgetProps> = (props) => {
  const system = useSystem();

  const handler = useJourneyNavigation(Routes.EDIT_USER);

  const { onSubmitOrCancel } = props;
  const { params } = handler;

  return (
    <AbstractEditDeveloperDialogWidget<DeveloperModel>
      handler={handler}
      contextProvider={() => {
        return {
          org_id: params.org_id,
          model_id: params.user_id
        };
      }}
      developerProvider={async (id) => {
        if (!id) {
          return null;
        }
        return system.developStore.resolve(id);
      }}
      initialValuesProvider={(developer) => {
        return {
          email: developer.model.email,
          roleIds: developer.roles.items
            .filter(({ role }) => role.options.role.org_id == params.org_id)
            .map(({ role }) => role.id)
        };
      }}
      formSubmitter={onSubmit}
      onSubmitOrCancel={onSubmitOrCancel}
    />
  );
};
